import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { governanceService } from "@/services/governance.service";

function AuditLogPage({ embedded = false }: { embedded?: boolean }) {
  const [tenantId, setTenantId] = useState("");
  const [userId, setUserId] = useState("");
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const queryParams = useMemo(
    () => ({
      page,
      limit,
      tenantId: tenantId || undefined,
      userId: userId || undefined,
      entity: entity || undefined,
      action: action || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    [action, endDate, entity, limit, page, startDate, tenantId, userId],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-audit-logs", queryParams],
    queryFn: () => governanceService.listAuditLogs(queryParams),
  });

  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHeader
          title="Audit logs"
          subtitle="Trace sensitive actions across tenants."
        />
      )}

      <div className="grid gap-3 lg:grid-cols-6">
        <input
          value={tenantId}
          onChange={(event) => {
            setTenantId(event.target.value);
            setPage(1);
          }}
          placeholder="Tenant ID"
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
        <input
          value={userId}
          onChange={(event) => {
            setUserId(event.target.value);
            setPage(1);
          }}
          placeholder="User ID"
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
        <input
          value={entity}
          onChange={(event) => {
            setEntity(event.target.value);
            setPage(1);
          }}
          placeholder="Entity"
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
        <input
          value={action}
          onChange={(event) => {
            setAction(event.target.value);
            setPage(1);
          }}
          placeholder="Action"
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
        <input
          type="date"
          value={startDate}
          onChange={(event) => {
            setStartDate(event.target.value);
            setPage(1);
          }}
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
        <input
          type="date"
          value={endDate}
          onChange={(event) => {
            setEndDate(event.target.value);
            setPage(1);
          }}
          className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Entity</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Entity ID</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                  Loading logs...
                </td>
              </tr>
            ) : data?.data.length ? (
              data.data.map((log) => (
                <tr
                  key={log.id}
                  className="border-b border-slate-100 last:border-none"
                >
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(log.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.tenantId}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.userId}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.entity}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.action}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.entityId}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                  No audit logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Page {data?.meta.page ?? page} of {data?.meta.totalPages ?? 1}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={Boolean(data) && page >= (data?.meta.totalPages ?? 1)}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default AuditLogPage;
